import { Component, OnInit, Input, AfterViewInit, Output, Inject, EventEmitter, ViewChild, ElementRef, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { SharedConstant } from '../../constants/sharedConstants';
import { SharedService } from '../../shared.service';
import { ManageEmpGrpService } from '../../services/manage-emp-grp-service/manage-emp-grp.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { EmtrPaginationValues, EmtrPagination } from '../../models/pagination-model';
import { InstrumentGroupService } from '../../services/instrument-group-service/instrument-group.service';
import { Select } from 'primeng/select';

enum DropdownType {
  EMPLOYEE_GROUP = 'groupName',
  INSTRUMENT_GROUP = 'instrumentGroup'
}

@Component({
  selector: 'ems-lazy-load-dropdown',
  templateUrl: './lazy-load-dropdown.component.html',
  styleUrl: './lazy-load-dropdown.component.scss'
})
export class LazyLoadDropdownComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() orgID: any
  @Input() name: any
  @Input() id: any
  @Input() dropdownName: any
  @Input() businessUnit: any
  @Input() ariaLabel = ''
  @Input() isAllOptionDisabled = false
  @Input() autoSelectFirstOption = false
  @Input() keepEmptyWhenNoData = false
  @Input() isDropdownDisabled = false
  @Input() resetToken: string | number = 0
  @Output() ngModelGroupChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateGroupOrPlanName: EventEmitter<any> = new EventEmitter<any>();
  @Output() groupsAvailable: EventEmitter<boolean> = new EventEmitter<boolean>();
  pageObject: EmtrPagination = EmtrPaginationValues;
  sharedConstant = SharedConstant;
  totalRecords = {
    groupName: 0,
    groupSearch: 0,
    InstGrp: 0,
    InstGrpSearch: 0,
  };
  selectedItemGroupName: any;
  selectedItemInstGroupName: any;
  empGroupDropDownOpts: any[] = [];
  instrumentGroupDropDownOpts = [];
  searchInputUpdate = new Subject<any>();
  searchInstrumentGroupUpdate = new Subject<any>();
  private destroy$ = new Subject<void>();
  groupPlanSearchValue: string;
  stateVal: any;
  onShowLoadData = false;
  @ViewChild('dropdown', { read: ElementRef }) dropdown: any;
  employeeGroupData = [];
  scrolledEle: any;
  scrollTopGlobal = -1;
  pageCount = 1;
  instrumentGroupSearchValue = '';
  instrumentGroupId: any;
  isInitialListUpdated: any;
  instPageCount = this.sharedConstant.defaultValue;
  instGrpSelectedValue: any;
  initialInstGrpLoadData = [];
  initialEmpGroupLoadData: any[] = [];
  instGroupClearApplied = false;

  constructor(private sharedService: SharedService,
    public manageEmpGrpService: ManageEmpGrpService,
    @Inject('entitlementConst') public entitlementConst,
    public instrumentGroupService: InstrumentGroupService) {
  }

  private hasValidOrgId(): boolean {
    return Number(this.orgID) > 0;
  }

  /**
   * Helper method to check if current dropdown is employee group type
   */
  private get isEmployeeGroupDropdown(): boolean {
    return this.dropdownName === DropdownType.EMPLOYEE_GROUP;
  }

  /**
   * Helper method to check if current dropdown is instrument group type
   */
  private get isInstrumentGroupDropdown(): boolean {
    return this.dropdownName === DropdownType.INSTRUMENT_GROUP;
  }

  ngOnInit() {
    this.stateVal = this.sharedService.getStateVal();
    this.pageObject.pageNo = 1;
    this.pageObject.pageSize = this.sharedConstant.defaultTableRecords;
    this.pageObject.sortField = ['name,asc'];
    const param = {
      paramObj: this.pageObject,
      groupName: ''
    };
    if (this.hasValidOrgId()) {
      if (this.stateVal && this.stateVal.filterObj && this.stateVal.filterObj.instrumentGroupId !== this.sharedConstant.defaultValue && this.isInstrumentGroupDropdown) {
        this.selectedItemGroupName = this.stateVal.filterObj.instrumentGroupId;
        this.instrumentGroupId = this.stateVal.filterObj.instrumentGroupId;
      }
      if (this.isEmployeeGroupDropdown) {
        if (this.sharedService.selectedGroupOrPlanName || (this.stateVal && this.stateVal.filterObj && this.stateVal.filterObj.groupName !== this.sharedConstant.groupNameAll)) {
          const grpNameVal = this.sharedService.selectedGroupOrPlanName ? this.sharedService.selectedGroupOrPlanName : this.stateVal.filterObj.groupName;
          this.selectedItemGroupName = this.groupPlanSearchValue = grpNameVal;
          if (this.sharedService.selectedGroupOrPlanName) {
            this.groupNameUpdated();
            this.updateGroupOrPlanName.emit();
          }
          else
            this.sharedService.selectedGroupOrPlanName = '';
        } else {
          this.selectedItemGroupName = this.sharedConstant.groupNameAll;
        }
      }
      if (this.isEmployeeGroupDropdown) {
        this.getEmployeeGroupList(param, 'initialLoad');
      } else if (this.sharedService.checkEntitlements(this.entitlementConst.navUUID.emtrAccess.instrumentsSummary.uuid, '', '') && this.isInstrumentGroupDropdown) {
        this.getInstrumentGroupList('', false, 'initialLoad');
      }
    }
    this.sharedService.groupNameDropDown.subscribe(value => {
      if (value === true) {
        this.groupNameUpdated();
      }
    });
    this.searchInputInsGroupName();
    this.searchInputGroupName();
    /* To clear the dropdown when clearAll button is clicked */
    this.clearButtonClicked();

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resetToken'] && !changes['resetToken'].firstChange) {
      // Parent increments resetToken to request a fresh dropdown state without remounting the component.
      this.handleResetTokenChange();
    }
  }

  private handleResetTokenChange(): void {
    // Clear transient UI state before rebuilding list/options from the initial API load.
    this.resetScrollState();
    this.onShowLoadData = false;

    if (this.isEmployeeGroupDropdown) {
      this.resetEmployeeGroupState();
      if (!this.hasValidOrgId()) {
        // notify parent that no selectable groups are available.
        this.groupsAvailable.emit(false);
        return;
      }

      const param = {
        paramObj: this.pageObject,
        groupName: ''
      };
      this.getEmployeeGroupList(param, 'initialLoad');
    } else if (this.isInstrumentGroupDropdown) {
      this.resetInstrumentGroupState();
      if (this.hasValidOrgId() && this.sharedService.checkEntitlements(this.entitlementConst.navUUID.emtrAccess.instrumentsSummary.uuid, '', '')) {
        this.getInstrumentGroupList('', false, 'initialLoad');
      }
    }
  }

  private resetEmployeeGroupState(): void {
    // Reset paging/options/selection so initial-load logic can repopulate deterministically.
    this.groupPlanSearchValue = '';
    this.pageCount = 1;
    this.pageObject.pageNo = 1;
    this.empGroupDropDownOpts = [];
    this.initialEmpGroupLoadData = [];
    this.totalRecords.groupName = 0;
    this.totalRecords.groupSearch = 0;
    this.selectedItemGroupName = this.keepEmptyWhenNoData ? '' : this.sharedConstant.groupNameAll;
  }

  private resetInstrumentGroupState(): void {
    // Reset instrument group state to the default "All" selection before initial reload.
    this.instrumentGroupSearchValue = '';
    this.instPageCount = this.sharedConstant.defaultValue;
    this.instrumentGroupDropDownOpts = [];
    this.initialInstGrpLoadData = [];
    this.totalRecords.InstGrp = 0;
    this.totalRecords.InstGrpSearch = 0;
    this.selectedItemGroupName = this.sharedConstant.defaultValue;
    this.instGrpSelectedValue = {
      label: this.sharedConstant.groupNameAll,
      value: this.sharedConstant.defaultValue
    };
  }
  searchInputGroupName() {
    this.searchInputUpdate
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        const param = {
          paramObj: this.pageObject,
          groupName: results ? results : ''
        };
        this.groupPlanSearchValue = results ?? '';
        if (this.hasValidOrgId()) {
          this.resetScrollState();
          param.paramObj.pageNo = 1;
          this.getEmployeeGroupList(param, 'search');
        }
      });
  }
  searchInputInsGroupName() {
    this.searchInstrumentGroupUpdate
      .pipe(debounceTime(500))
      .pipe(distinctUntilChanged())
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        if (this.hasValidOrgId()) {
          this.resetScrollState();
          this.instrumentGroupSearchValue = results ?? '';
          this.getInstrumentGroupList(results, true, 'search');
        }
      });
  }
  ngAfterViewInit() {
    if (this.isEmployeeGroupDropdown) {
      if (!this.selectedItemGroupName && !this.keepEmptyWhenNoData) {
        this.selectedItemGroupName = this.sharedConstant.groupNameAll;
      } if (this.sharedService.selectedGroupOrPlanName) {
        this.selectedItemGroupName = this.sharedService.selectedGroupOrPlanName;
        this.sharedService.selectedGroupOrPlanName = '';
      }
    }
    if (this.isInstrumentGroupDropdown && !this.instrumentGroupId) {
      this.selectedItemGroupName = this.sharedConstant.defaultValue;
      // Ensure instGrpSelectedValue is set
      if (!this.instGrpSelectedValue) {
        this.instGrpSelectedValue = {
          label: this.sharedConstant.groupNameAll,
          value: this.sharedConstant.defaultValue
        };
      }
    }
  }

  /**
   * Gets the current dropdown options array based on dropdown type
   * @returns Array of dropdown options for employee or instrument groups
   */
  optionsToLoad() {
    if (this.isEmployeeGroupDropdown) {
      return this.empGroupDropDownOpts;
    } else if (this.isInstrumentGroupDropdown) {
      return this.instrumentGroupDropDownOpts;
    }
  }
  totalRecordsToLoad() {
    if (this.isEmployeeGroupDropdown) {
      return this.totalRecords.groupSearch;
    } else if (this.isInstrumentGroupDropdown) {
      return this.totalRecords.InstGrpSearch;
    }
  }

  /**
   * Prepares filter object for employee group API call
   */
  private prepareEmployeeGroupFilter(param: any, type?: string): any {
    const filterObj = {
      orgId: this.orgID,
      groupName: param?.groupName ? param.groupName : this.groupPlanSearchValue,
      active: true
    };

    if (type === 'initialLoad') {
      filterObj.groupName = '';
    }

    return filterObj;
  }

  /**
   * Prepares pagination parameters for employee group API call
   */
  private prepareEmployeeGroupPagination(param: any, type?: string): any {
    if (type === 'scroll') {
      param.pageNo = this.pageCount;
    }

    if (type === 'search') {
      this.pageObject.pageNo = this.pageCount = 1;
    }

    return type === 'scroll' ? param : param.paramObj;
  }

  /**
   * Transforms API response data to dropdown option format
   */
  private transformEmployeeGroupData(content: any[]): any[] {
    return content.map(empGroupName => ({
      label: empGroupName.name,
      value: empGroupName.name,
      id: empGroupName.id,
      type: empGroupName.type
    }));
  }

  /**
   * Handles selected state and "All" option for employee groups
   */
  private processEmployeeGroupSelection(scrollData: any[]): void {
    const stateValue = scrollData.some(item => item.value === this.selectedItemGroupName);
    const allOption = {
      label: this.sharedConstant.groupNameAll,
      value: this.sharedConstant.groupNameAll,
      id: null,
      type: '',
      disabled: this.isAllOptionDisabled && scrollData.length > 0
    };

    if (this.empGroupDropDownOpts.length === 0 ||
      (this.empGroupDropDownOpts.length > 0 && this.empGroupDropDownOpts[0] &&
        this.empGroupDropDownOpts[0].value !== this.sharedConstant.groupNameAll)) {

      if (this.sharedService.selectedGroupOrPlanName ||
        (this.stateVal && this.stateVal.filterObj && this.stateVal.filterObj.groupName !== this.sharedConstant.groupNameAll && !stateValue) ||
        (this.selectedItemGroupName && this.selectedItemGroupName !== this.sharedConstant.groupNameAll && !stateValue)) {
        const groupName = this.sharedService.selectedGroupOrPlanName || this.stateVal?.filterObj?.groupName || this.selectedItemGroupName;
        if (groupName && groupName !== this.sharedConstant.groupNameAll) {
          this.empGroupDropDownOpts.unshift({
            label: groupName,
            value: groupName,
            id: null,
            type: ''
          });
          this.selectedItemGroupName = groupName;
        }
      } else if (stateValue) {
        const ind = scrollData.findIndex(item => item.value === this.selectedItemGroupName);
        const [objectToMove] = scrollData.splice(ind, 1);
        scrollData.unshift(objectToMove);
        const stateGroupName = this.stateVal?.filterObj?.groupName;
        if (stateGroupName && stateGroupName !== this.sharedConstant.groupNameAll) {
          this.selectedItemGroupName = stateGroupName;
        }
      }
      this.empGroupDropDownOpts.unshift({ ...allOption });
      this.initialEmpGroupLoadData.unshift({ ...allOption });
    }
  }

  // During initial employee-group load, auto-pick the first real group when
  // snapshot-style behavior is enabled (All disabled) or explicit auto-select is requested.
  private applyDefaultEmployeeGroupSelection(scrollData: any[], type?: string): void {
    if (!this.isEmployeeGroupDropdown || type !== 'initialLoad' || scrollData.length === 0) {
      return;
    }

    if (!this.autoSelectFirstOption && !this.isAllOptionDisabled) {
      return;
    }

    this.selectedItemGroupName = scrollData[0].value;
    this.groupNameUpdated();
  }

  // Handles initial no-data state for employee-group dropdowns.
  // Returns true when no records were found and default selection was applied:
  // Employee Association mode (keepEmptyWhenNoData enabled) stays empty; all other modes default to "All".
  private applyNoDataEmployeeGroupSelection(type?: string): boolean {
    if (!this.isEmployeeGroupDropdown || type !== 'initialLoad' || this.totalRecords.groupSearch > 0) {
      return false;
    }

    const hasEmployeeAssociationBehavior = this.keepEmptyWhenNoData;
    this.selectedItemGroupName = hasEmployeeAssociationBehavior ? '' : this.sharedConstant.groupNameAll;

    if (hasEmployeeAssociationBehavior) {
      this.empGroupDropDownOpts = [];
      this.initialEmpGroupLoadData = [];
    } else {
      const allOption = {
        label: this.sharedConstant.groupNameAll,
        value: this.sharedConstant.groupNameAll
      };
      this.empGroupDropDownOpts = [{ ...allOption }];
      this.initialEmpGroupLoadData = [{ ...allOption }];
    }

    this.groupNameUpdated();
    return true;
  }

  /**
   * Updates dropdown options based on load type
   */
  private updateEmployeeGroupOptions(scrollData: any[], type?: string, records?: number): void {
    const allOption = {
      label: this.sharedConstant.groupNameAll,
      value: this.sharedConstant.groupNameAll,
      id: null,
      type: '',
      disabled: this.isAllOptionDisabled && ((this.totalRecords.groupName || 0) > 0 || scrollData.length > 0)
    };

    if (type === 'initialLoad') {
      this.empGroupDropDownOpts = [...this.empGroupDropDownOpts, ...scrollData];
      this.initialEmpGroupLoadData = [
        ...this.initialEmpGroupLoadData,
        ...scrollData.map(item => ({ label: item.label, value: item.value, id: item.id, type: item.type }))
      ];
      this.totalRecords.groupName = records;
    } else {
      this.empGroupDropDownOpts = (type === 'search'
        ? [{ ...allOption }, ...scrollData]
        : [...this.empGroupDropDownOpts, ...scrollData]);

      if (type !== 'search') {
        const allOptionCopy = { label: this.sharedConstant.groupNameAll, value: this.sharedConstant.groupNameAll, id: null, type: '' };
        const scrollDataCopy = scrollData.map(item => ({ label: item.label, value: item.value, id: item.id, type: item.type }));
        this.initialEmpGroupLoadData = [...this.initialEmpGroupLoadData, ...scrollDataCopy];
      }
      this.onShowLoadData = true;
    }
  }

  getEmployeeGroupList(param, type?, scrollToIndex?) {
    if (!this.hasValidOrgId()) {
      if (type === 'initialLoad') {
        this.groupsAvailable.emit(false);
      }
      return;
    }

    const filterObj = this.prepareEmployeeGroupFilter(param, type);
    const paginationParam = this.prepareEmployeeGroupPagination(param, type);

    this.manageEmpGrpService.getEmployeeGrpOrPlanList(paginationParam, filterObj).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (value: any) => {
        if (!value || !value.data || !Array.isArray(value.data.content)) {
          if (type === 'initialLoad') {
            this.groupsAvailable.emit(false);
          }
          return;
        }

        if (type === 'initialLoad') {
          this.groupsAvailable.emit(value.data.totalElements > 0);
        }

        this.totalRecords.groupSearch = value.data.totalElements;

        if (this.applyNoDataEmployeeGroupSelection(type)) {
          return;
        }

        const scrollData = this.transformEmployeeGroupData(value.data.content);

        this.processEmployeeGroupSelection(scrollData);
        this.updateEmployeeGroupOptions(scrollData, type, value.data.totalElements);
        this.applyDefaultEmployeeGroupSelection(scrollData, type);

        if (scrollToIndex) {
          this.scrollOnLoad(scrollToIndex - 10);
        }
      },
      error: () => {
        if (type === 'initialLoad') {
          this.groupsAvailable.emit(false);
        }
      }
    });
  }

  /**
   * Gets the dropdown panel element for scroll operations
   */
  private getDropdownPanel(): HTMLElement | null {
    if (!this.dropdown?.nativeElement) {
      return null;
    }
    return this.dropdown.nativeElement.querySelector('.p-select-overlay > .p-select-list-container > p-scroller > .p-virtualscroller');
  }

  scrollToIndex(index) {
    setTimeout(() => {
      if (this.dropdown && this.dropdown.viewPort) {
        this.dropdown.viewPort.scrollToIndex(index);
      }
    }, 0);
  }

  scrollOnLoad(index) {
    setTimeout(() => {
      const panel = this.getDropdownPanel();
      if (panel) {
        panel.scrollTop = index;
      }
    }, 0);
  }

  attachScrollEvent() {
    setTimeout(() => {
      const panel = this.getDropdownPanel();
      if (panel) {
        panel.onscroll = event => this.loadItems(event as Event);
      }
    }, 0);
  }
  groupNameUpdated() {
    if (this.isEmployeeGroupDropdown) {
      const selectedOption = this.getSelectedEmployeeGroupOption();
      this.ngModelGroupChange.emit({
        groupName: this.sharedConstant.groupName,
        selectedItem: this.selectedItemGroupName,
        id: selectedOption?.id ?? null,
        type: selectedOption?.type ?? ''
      });
      this.groupPlanSearchValue = this.selectedItemGroupName === this.sharedConstant.groupNameAll ? this.sharedConstant.groupNameAll: '';
      this.totalRecords.groupSearch = this.totalRecords.groupName;
    } else if (this.isInstrumentGroupDropdown) {
      this.ngModelGroupChange.emit({ groupName: this.sharedConstant.instrumentGroup, selectedItem: this.selectedItemGroupName });
      this.instrumentGroupSearchValue = '';
      this.totalRecords.InstGrpSearch = this.totalRecords.InstGrp;
      this.instGrpSelectedValue = this.instrumentGroupDropDownOpts.find(item => item.value === this.selectedItemGroupName);
      this.sharedService.stateValues.instrumentGrpSelected = this.instGrpSelectedValue?.label;
      this.sharedService.setStateVal(JSON.stringify(this.sharedService.stateValues));
      this.initialInstGrpLoadData = this.removeDuplicates(this.initialInstGrpLoadData, 'value');
    }
  }

  isDropdownOptionDisabled(option: { disabled?: boolean } | null | undefined): boolean {
    return !!option?.disabled;
  }

  /**
   * Checks if we've reached the end of the data for the current dropdown type
   */
  private hasReachedEndOfData(): boolean {
    if (this.isEmployeeGroupDropdown) {
      return (this.empGroupDropDownOpts.length - 1) >= this.totalRecords.groupSearch;
    } else if (this.isInstrumentGroupDropdown) {
      return (this.instrumentGroupDropDownOpts.length - 1) >= this.totalRecords.InstGrpSearch;
    }
    return false;
  }

  /**
   * Checks if we need to load more items based on scroll position
   */
  private shouldLoadMoreItems(scrollTo: number): boolean {
    const currentScrollTop = Math.trunc(this.scrolledEle.scrollTop);
    const reachedBottom = currentScrollTop >= (scrollTo - 1);
    const scrollConditionsMet = (this.scrollTopGlobal !== scrollTo) && reachedBottom;

    if (this.isEmployeeGroupDropdown) {
      return scrollConditionsMet && (this.empGroupDropDownOpts.length - 1) < this.totalRecords.groupSearch;
    } else if (this.isInstrumentGroupDropdown) {
      return scrollConditionsMet && (this.instrumentGroupDropDownOpts.length - 1) < this.totalRecords.InstGrpSearch;
    }
    return false;
  }

  /**
   * Loads more items for the appropriate dropdown type
   */
  private loadMoreItems(scrollTo: number): void {
    if (this.isEmployeeGroupDropdown) {
      this.pageCount += 1;
      this.getEmployeeGroupList(this.pageObject, 'scroll', scrollTo);
    } else if (this.isInstrumentGroupDropdown) {
      this.instPageCount += 1;
      this.getInstrumentGroupList('', false, 'scroll', scrollTo);
    }

    this.scrollTopGlobal = scrollTo;
  }

  loadItems(event) {
    this.scrolledEle = event.target;

    // Check if we've reached the end of data
    if (this.hasReachedEndOfData()) {
      return;
    }

    const scrollTo = this.scrolledEle.scrollHeight - this.scrolledEle.offsetHeight;

    if (this.shouldLoadMoreItems(scrollTo)) {
      this.loadMoreItems(scrollTo);
    }
  }
  searchedText(event) {
    if (this.isEmployeeGroupDropdown) {
      this.groupPlanSearchValue = event.target.value;
      this.searchInputUpdate.next(this.groupPlanSearchValue);
    }
    else if (this.isInstrumentGroupDropdown) {
      this.instrumentGroupSearchValue = event.target.value;
      this.searchInstrumentGroupUpdate.next(this.instrumentGroupSearchValue);
    }
  }

  clearSearch(dropdown: Select) {
    if (this.selectedItemGroupName !== this.sharedConstant.groupNameAll && this.isEmployeeGroupDropdown) {
      this.empGroupDropDownOpts = this.scrollSelectedValueTop(this.initialEmpGroupLoadData);
      if (!this.empGroupDropDownOpts.find(item => item.value === this.selectedItemGroupName)) {
        this.empGroupDropDownOpts.push({
          label: this.selectedItemGroupName,
          value: this.selectedItemGroupName,
          id: null,
          type: ''
        });
        this.empGroupDropDownOpts = this.scrollSelectedValueTop(this.empGroupDropDownOpts, 'selected');
      }
      this.scrollOnLoad(0);
    } else if (this.selectedItemGroupName !== this.sharedConstant.defaultValue && this.isInstrumentGroupDropdown) {
      this.instrumentGroupDropDownOpts = this.scrollSelectedValueTop(this.initialInstGrpLoadData);
      if (!this.instrumentGroupDropDownOpts.find(item => item.value === this.selectedItemGroupName)) {
        this.instrumentGroupDropDownOpts.push({
          label: this.instGrpSelectedValue?.label || 'All',
          value: this.instGrpSelectedValue?.value || this.selectedItemGroupName
        });
        this.instrumentGroupDropDownOpts = this.scrollSelectedValueTop(this.instrumentGroupDropDownOpts, 'selected');
      }
      this.scrollOnLoad(0);
    }
    if (!this.groupPlanSearchValue && this.isEmployeeGroupDropdown) {
      this.resetScrollState();
      this.pageCount = 1;
      dropdown.resetFilter();
    }
    if (!this.instrumentGroupSearchValue && this.isInstrumentGroupDropdown) {
      this.resetScrollState();
      this.instPageCount = 0;
      dropdown.resetFilter();
    }
    this.attachScrollEvent();
  }

  private resetScrollState(): void {
    this.scrollTopGlobal = -1;
    this.scrolledEle = null;
  }

  getSelectedEmployeeGroupOption(): any {
    if (!this.isEmployeeGroupDropdown || !this.selectedItemGroupName) {
      return null;
    }

    return this.empGroupDropDownOpts.find(item => item.value === this.selectedItemGroupName) || null;
  }

  resetDropdown(): void {
    if (!this.isEmployeeGroupDropdown) {
      return;
    }

    this.groupPlanSearchValue = '';
    this.selectedItemGroupName = this.sharedConstant.groupNameAll;
    this.totalRecords.groupSearch = this.totalRecords.groupName;
    this.resetScrollState();

    if (this.initialEmpGroupLoadData?.length > 0) {
      this.empGroupDropDownOpts = [...this.initialEmpGroupLoadData];
    }

    const hasAllOption = this.empGroupDropDownOpts.some(item => item.value === this.sharedConstant.groupNameAll);
    if (!hasAllOption) {
      this.empGroupDropDownOpts.unshift({
        label: this.sharedConstant.groupNameAll,
        value: this.sharedConstant.groupNameAll,
        id: null,
        type: ''
      });
    }
  }

  scrollSelectedValueTop(groupData, selected?) {
    if (!groupData || groupData.length === 0) {
      return groupData;
    }
    // Use original data for instrument groups, otherwise use current data
    let originalData: any;
    if (this.isEmployeeGroupDropdown) {
      originalData = (this.isEmployeeGroupDropdown && this.initialEmpGroupLoadData?.length > 0 && !selected)
        ? [...this.initialEmpGroupLoadData]
        : [...groupData];
    } else if (this.isInstrumentGroupDropdown) {
      originalData = (this.isInstrumentGroupDropdown && this.initialInstGrpLoadData?.length > 0 && !selected)
        ? [...this.initialInstGrpLoadData]
        : [...groupData];
    }
    // Find selected item index
    const selectedIndex = originalData.findIndex(item => item.value === this.selectedItemGroupName);
    // If not found or already at top, return original
    if (selectedIndex <= 0) {
      return originalData;
    }
    // Move selected item to top (after "All" if present)
    const [selectedItem] = originalData.splice(selectedIndex, 1);
    const insertPosition = (originalData[0]?.value === this.sharedConstant.groupNameAll || originalData[0]?.value === this.sharedConstant.defaultValue) ? 1 : 0;
    originalData.splice(insertPosition, 0, selectedItem);
    return originalData;
  }

  /**
   * Determines instrument group name based on parameters
   */
  private determineInstrumentGroupName(param: string, isSearch: boolean): string {
    if (param === 'reset') {
      return '';
    } else if (isSearch) {
      return param;
    } else {
      return this.stateVal && this.stateVal.instrumentSearchedKey ? this.stateVal.instrumentSearchedKey : '';
    }
  }

  /**
   * Prepares filter object for instrument group API call
   */
  private prepareInstrumentGroupFilter(instrumentGroupName: string, onload?: string): any {
    return {
      name: instrumentGroupName ? (onload === 'initialLoad' ? '' : instrumentGroupName) : this.instrumentGroupSearchValue,
      businessUnit: this.businessUnit,
      active: true
    };
  }

  /**
   * Prepares pagination object for instrument group API call
   */
  private prepareInstrumentGroupPagination(isSearch: boolean, onload?: string): any {
    if (isSearch) {
      this.pageObject.pageNo = this.instPageCount = 0;
    }

    const pageObj = Object.assign({}, this.pageObject);
    pageObj.sortField = ['name,asc'];
    pageObj.pageNo = onload === 'scroll' ? this.instPageCount : ((onload === 'initialLoad' || onload === 'search') ? 0 : pageObj.pageNo);

    return pageObj;
  }

  /**
   * Transforms instrument group API response to dropdown format
   */
  private transformInstrumentGroupData(content: any[]): any[] {
    return content.map(instrumentGroup => ({
      label: instrumentGroup.name,
      value: instrumentGroup.id
    }));
  }

  /**
   * Handles selected state and "All" option for instrument groups
   */
  private processInstrumentGroupSelection(scrollData: any[]): void {
    if (this.instrumentGroupDropDownOpts.length === 0 ||
      (this.instrumentGroupDropDownOpts.length > 0 && this.instrumentGroupDropDownOpts[0] &&
        this.instrumentGroupDropDownOpts[0].label !== this.sharedConstant.groupNameAll)) {

      if (this.stateVal.filterObj && this.stateVal.filterObj.instrumentGroupId &&
        !scrollData.find(item => item.value === this.stateVal.filterObj.instrumentGroupId)) {
        this.selectedItemGroupName = this.stateVal.filterObj.instrumentGroupId;
        this.instGrpSelectedValue = {
          label: this.stateVal.instrumentGrpSelected,
          value: this.stateVal.filterObj.instrumentGroupId
        };
        this.instrumentGroupDropDownOpts.unshift(this.instGrpSelectedValue);
        this.initialInstGrpLoadData.unshift({
          label: this.instGrpSelectedValue.label,
          value: this.instGrpSelectedValue.value
        });
      } else if (this.stateVal.filterObj && this.stateVal.filterObj.instrumentGroupId &&
        scrollData.find(item => item.value === this.stateVal.filterObj.instrumentGroupId)) {
        const ind = scrollData.findIndex(item => item.value === this.stateVal.filterObj.instrumentGroupId);
        const [objectToMove] = scrollData.splice(ind, 1);
        scrollData.unshift(objectToMove);
        this.instGrpSelectedValue = {
          label: this.stateVal.instrumentGrpSelected,
          value: this.stateVal.filterObj.instrumentGroupId
        };
        this.selectedItemGroupName = this.stateVal.filterObj.instrumentGroupId;
        this.initialInstGrpLoadData.unshift({
          label: this.instGrpSelectedValue.label,
          value: this.instGrpSelectedValue.value
        });
      }

      // Add "All" option with separate objects
      const allOption = { label: this.sharedConstant.groupNameAll, value: this.sharedConstant.defaultValue };
      this.instrumentGroupDropDownOpts.unshift({ ...allOption });
      this.initialInstGrpLoadData.unshift({ ...allOption });
    }
  }

  /**
   * Updates instrument group dropdown options based on load type
   */
  private updateInstrumentGroupOptions(scrollData: any[], onload?: string, records?: number): void {
    if (onload === 'initialLoad') {
      this.instrumentGroupDropDownOpts = [...this.instrumentGroupDropDownOpts, ...scrollData];

      // Set default "All" selection if no specific instrument group is selected
      if (!this.stateVal.filterObj?.instrumentGroupId || this.stateVal.filterObj?.instrumentGroupId === this.sharedConstant.defaultValue) {
        this.instGrpSelectedValue = {
          label: this.sharedConstant.groupNameAll,
          value: this.sharedConstant.defaultValue
        };
        this.selectedItemGroupName = this.sharedConstant.defaultValue;
      }

      this.initialInstGrpLoadData = [
        ...this.initialInstGrpLoadData,
        ...scrollData.map(item => ({ label: item.label, value: item.value }))
      ];
      this.totalRecords.InstGrp = records;
    } else {
      this.instrumentGroupDropDownOpts = (onload === 'search'
        ? [{ label: this.sharedConstant.groupNameAll, value: this.sharedConstant.defaultValue }, ...scrollData]
        : [...this.instrumentGroupDropDownOpts, ...scrollData]);

      if (onload !== 'search' || this.instGroupClearApplied) {
        const scrollDataCopy = scrollData.map(item => ({ label: item.label, value: item.value }));
        this.initialInstGrpLoadData = this.instGroupClearApplied
          ? [{ label: this.sharedConstant.groupNameAll, value: this.sharedConstant.defaultValue }, ...this.initialInstGrpLoadData, ...scrollDataCopy]
          : [...this.initialInstGrpLoadData, ...scrollDataCopy];
        this.instGroupClearApplied = false;
      }
      this.onShowLoadData = true;
    }

    if (this.dropdown && this.dropdown.options) {
      this.dropdown.options = this.instrumentGroupDropDownOpts;
    }
  }

  getInstrumentGroupList(param: string, isSearch: boolean, onload?: string, scrollToIndex?: number) {
    const instrumentGroupName = this.determineInstrumentGroupName(param, isSearch);
    this.isInitialListUpdated = !!instrumentGroupName;

    const defaultObj = this.prepareInstrumentGroupFilter(instrumentGroupName, onload);
    const pageObj = this.prepareInstrumentGroupPagination(isSearch, onload);

    this.sharedService.stateValues.instrumentSearchedKey = instrumentGroupName;
    this.sharedService.setStateVal(JSON.stringify(this.sharedService.stateValues));

    this.instrumentGroupService.getInstrumentGroupList(pageObj, defaultObj, this.sharedService.pageName).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response) => {
        if (!response || !response.data || !response.data.content) {
          return;
        }

        this.totalRecords.InstGrpSearch = response.data.totalElements;
        const scrollData = this.transformInstrumentGroupData(response.data.content);

        this.processInstrumentGroupSelection(scrollData);
        this.updateInstrumentGroupOptions(scrollData, onload, response.data.totalElements);

        if (scrollToIndex) {
          this.scrollOnLoad(scrollToIndex - 10);
        }
      },
      error: error => {
        return this.instrumentGroupDropDownOpts;
      }
    });
  }

  clearFilter(landingPage?) {
    if (landingPage === 'RulesLanding') {
      if (this.isEmployeeGroupDropdown) {
        this.ngModelGroupChange.emit({ groupName: this.sharedConstant.groupName, selectedItem: this.sharedConstant.groupNameAll });
        this.selectedItemGroupName = this.sharedConstant.groupNameAll;
        this.initialInstGrpLoadData = [];
      } else if (this.isInstrumentGroupDropdown) {
        const defaultInstGrpValue = { label: this.sharedConstant.groupNameAll, value: this.sharedConstant.defaultValue };
        this.ngModelGroupChange.emit({ groupName: this.sharedConstant.instrumentGroup, selectedItem: this.sharedConstant.defaultValue });
        this.selectedItemGroupName = this.sharedConstant.defaultValue;
        this.instGrpSelectedValue = defaultInstGrpValue;
        this.initialInstGrpLoadData = [];
      }
    }
  }
  private removeDuplicates(array: any[], key = 'value'): any[] {
    const seen = new Set();
    return array.filter(item => {
      const duplicate = seen.has(item[key]);
      seen.add(item[key]);
      return !duplicate;
    });
  }
  clearButtonClicked() {
    this.sharedService.clearDropdownValue.pipe(
      takeUntil(this.destroy$)
    ).subscribe((result) => {
      if (result?.type === this.sharedConstant.dropDownClearOption.clear) {
        if (this.isEmployeeGroupDropdown) {
          this.groupPlanSearchValue = '';
          this.searchInputUpdate.next('');
        }
        if (this.isInstrumentGroupDropdown) {
          this.instrumentGroupSearchValue = '';
          this.instGroupClearApplied = true;
          this.searchInstrumentGroupUpdate.next('');
        }
        this.clearFilter(result?.landingPage);
      }
      if (result?.type === this.sharedConstant.dropDownClearOption.destroy) {
        this.clearFilter(result?.landingPage);
      }
    });
  }
  ngOnDestroy(): void {
    const panel = this.getDropdownPanel();
    if (panel) {
      panel.onscroll = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
    this.sharedService.clearDropdownValue.next({ type: this.sharedConstant.dropDownClearOption.destroy, landingPage: this.sharedConstant.dropDownClearOption.landingPage });
    this.sharedService.clearDropdownValue.next(null);
  }
}
